import { ProductServiceStatus } from '../../../license/types/ProductServiceStatus';
import { MdrServiceFlavor } from '../../../license/types/MdrServiceFlavor';
import { translateFn } from '../../../utils/translator.helper';
import moment from 'moment-timezone';
import { TimestampFormat } from '@nirvana/shared/time';
import { getTimezone } from '../../../utils/global.utils';
import { LicenseProductStatus } from '../licensingGrid/types/licensing-grid-license';
import { LicenseRenderers } from '../licenseRenderers/LicenseRenderers';
import { LicenseAddon, ProductService } from '../../types/company-data';

export function getStatusWithBullet(mdrStatus: ProductServiceStatus | undefined) {
  let bullet;
  let mdrStatusText: string;

  switch (mdrStatus) {
    case ProductServiceStatus.ACTIVE:
      mdrStatusText = translateFn('Active');
      bullet = 'bullet-success';
      break;
    case ProductServiceStatus.INACTIVE:
      mdrStatusText = translateFn('Inactive');
      bullet = 'bullet-red';
      break;
    case ProductServiceStatus.ONBOARDING:
      mdrStatusText = translateFn('Onboarding');
      bullet = 'bullet-transparent';
      break;
    case ProductServiceStatus.SWITCHING:
      mdrStatusText = translateFn('Switching flavor');
      bullet = 'bullet-grey';
      break;
    case ProductServiceStatus.DISABLING:
      mdrStatusText = translateFn('Disabling');
      bullet = 'bullet-orange';
      break;
    case ProductServiceStatus.ENROLLING:
      mdrStatusText = translateFn('Enrolling');
      bullet = 'bullet-transparent';
      break;
    default:
      mdrStatusText = 'N/A';
      bullet = 'bullet-neutral';
      break;
  }

  return { bullet, mdrStatusText };
}

export function getMdrWarrantyStatusWithBullet(mdrWarrantyStatus: LicenseProductStatus) {
  const warrantyBullet = LicenseProductStatus.getBulletStatusClass(mdrWarrantyStatus);
  const warrantyStatusText = LicenseRenderers.getTranslatedProductStatus(mdrWarrantyStatus);

  return { warrantyBullet, warrantyStatusText };
}

const MdrServiceFlavorStrings: { [key in MdrServiceFlavor]: string } = {
  [MdrServiceFlavor.FOUNDATIONS]: 'MDR Foundations for MSP',
  [MdrServiceFlavor.FOUNDATIONS_LEGACY]: 'MDR Foundations',
  [MdrServiceFlavor.PREMIUM]: 'MDR Premium',
  [MdrServiceFlavor.ENTERPRISE]: 'MDR Enterprise',
  [MdrServiceFlavor.BITDEFENDER_MDR]: 'Bitdefender MDR',
  [MdrServiceFlavor.BITDEFENDER_MDR_PLUS]: 'Bitdefender MDR Plus',
};

export function getMdrServiceFlavorString(value: number): string | undefined {
  return MdrServiceFlavorStrings[value as MdrServiceFlavor];
}

export function getMdrServiceDateString(mdrProductService: ProductService): string {
  if (mdrProductService.status === ProductServiceStatus.ONBOARDING) {
    return 'N/A';
  }

  const formattedStartDate = mdrProductService.activeSince
    ? moment.utc(mdrProductService.activeSince).tz(getTimezone()).format(TimestampFormat.DATE_ONLY)
    : null;

  const formattedEndDate = mdrProductService.statusEndDate
    ? moment.utc(mdrProductService.statusEndDate).tz(getTimezone()).format(TimestampFormat.DATE_ONLY)
    : null;

  return formattedStartDate && formattedEndDate ? `${formattedStartDate} - ${formattedEndDate}` : 'N/A';
}

export function getMdrWarrantyDateString(breachWarrantyAddon: LicenseAddon): string {
  const formattedStartDate = breachWarrantyAddon.created
    ? moment.utc(breachWarrantyAddon.created).tz(getTimezone()).format(TimestampFormat.DATE_ONLY)
    : null;

  const formattedEndDate = breachWarrantyAddon.expiryDate
    ? moment.utc(breachWarrantyAddon.expiryDate).tz(getTimezone()).format(TimestampFormat.DATE_ONLY)
    : null;

  return formattedStartDate && formattedEndDate ? `${formattedStartDate} - ${formattedEndDate}` : 'N/A';
}
